// Beginner Bundles
class BeginnerBundle extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._button = null;
    // DOM-dependent properties initialized in connectedCallback
    this._title = '';
    this.cart = null;
    this.error = false;
    this.hideErrors = this.hideErrors || false;
  }

  connectedCallback() {
    // Find the add-to-cart button
    this._button = this.querySelector('.beginner-bundle__submit');
    // Cache title and cart now that element is connected
    const titleEl = this.querySelector('h4');
    this._title = titleEl ? titleEl.textContent.trim() : '';
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (this._button) {
      this._button.addEventListener('click', this._onClick);
    }
  }

  disconnectedCallback() {
    if (this._button) {
      this._button.removeEventListener('click', this._onClick);
    }
  }

  async _onClick(e) {
    e.preventDefault();

    const addToCartButton = this._button;
    if (!addToCartButton) return;

    // Disable the button to prevent multiple clicks
    addToCartButton.disabled = true;
    const originalText = addToCartButton.textContent;
    addToCartButton.textContent = 'Adding...';

    let itemsToAdd = [];

    const allVariants = this.querySelectorAll('.beginner-bundle__variant');
    const primaryVariantId = allVariants.length ? allVariants[0].getAttribute('data-variant-id') : null;

    if (primaryVariantId === null) {
      console.error('BeginnerBundles: primary variant ID not found');
      // Restore button state before returning
      addToCartButton.disabled = false;
      addToCartButton.textContent = originalText || 'Add Bundle to Cart';
      return;
    }

    allVariants.forEach((variant, index, array) => {
      const variantId = variant.getAttribute('data-variant-id');
      const quantity = variant.getAttribute('data-quantity') || '1';

      if (!variantId) return;

      itemsToAdd.push({
        id: variantId,
        quantity: parseInt(quantity, 10) || 1,
        parent_id: variantId === primaryVariantId ? null : primaryVariantId,
        properties: {
          Bundle: this._title,
          '_beginner-bundle': variantId === primaryVariantId ? 'parent' : 'child',
        },
      });
    });

    if (!itemsToAdd.length) {
      console.error('BeginnerBundles: bundle has no variants to add');
      // Restore button state before returning
      addToCartButton.disabled = false;
      addToCartButton.textContent = originalText || 'Add Bundle to Cart';
      return;
    }

    const bundleVariantIds = itemsToAdd.map((item) => item.id).join(', ');

    let body;
    if (this.cart) {
      body = {
        items: itemsToAdd,
        sections: this.cart.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      };
      this.cart.setActiveElement(document.activeElement);
    } else {
      body = { items: itemsToAdd };
    }

    try {
      const response = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch((err) => {
        console.error('Invalid JSON from cart add response', err);
        return null;
      });

      if (!data) {
        this.handleErrorMessage('Unexpected response from server.');
        this.error = true;
        addToCartButton.setAttribute('aria-disabled', 'true');
        addToCartButton.textContent = 'Error';
        return;
      }

      if (data.errors) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'beginner-bundle',
          bundleVariantIds: bundleVariantIds,
          bundleTitle: this._title,
          errors: data.errors || data.description,
          message: data.message,
        });
        this.handleErrorMessage(data.errors || data.message);

        addToCartButton.setAttribute('aria-disabled', 'true');
        addToCartButton.textContent = 'Sold Out';

        this.error = true;
        return;
      }

      if (!this.cart) {
        window.location = window.routes.cart_url;
        return;
      }

      if (!this.error) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'beginner-bundle',
          bundleVariantIds: bundleVariantIds,
          bundleTitle: this._title,
          cartData: data,
        });
      }

      this.cart.renderContents(data);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      // Re-enable only if no error (leave Sold Out / Error state if error)
      if (!this.error) {
        addToCartButton.disabled = false;
        addToCartButton.textContent = originalText || 'Add Bundle to Cart';
        if (this._button) this._button.removeAttribute('aria-disabled');
      }
      if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
    }
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessageWrapper =
      this.errorMessageWrapper || this.querySelector('.beginner-bundle__error-message-wrapper');
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.beginner-bundle__error-message');

    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }
}

// Register the custom element (if not already registered)
if (!customElements.get('beginner-bundle')) {
  customElements.define('beginner-bundle', BeginnerBundle);
}
